import { MOTION } from '@/components/common/motion';
import { ArrowLeft } from '@phosphor-icons/react';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface PageHeaderProps {
  /** The page's one `h1`. Rendered as the black-to-gray gradient wordmark. */
  title: string;
  /**
   * Takes the place of the default `h1` — for a title the page lets the user edit in place.
   *
   * The slot owns the heading: it has to render the page's one `h1` itself, and
   * `PAGE_TITLE_CLASSES` is exported so it looks the same as every other page's title. `title`
   * stays required, because it is still the plain-text name of the page for the caller to fall
   * back on.
   */
  titleSlot?: ReactNode;
  /** A line under the title — live counts, a timestamp, a short description. */
  subtitle?: ReactNode;
  /** Renders a back link above the title. `label` is the visible text, so translate it. */
  backTo?: { to: string; label: string };
  /** Page-level actions, right-aligned on the title row and wrapping under it on a phone. */
  actions?: ReactNode;
  /** Set it when a `section` elsewhere on the page points at the title with `aria-labelledby`. */
  titleId?: string;
}

/** The back link: quiet until hovered, and on its own line so the title starts at the margin. */
const BACK_LINK_CLASSES = `inline-flex w-fit items-center gap-2 rounded-lg py-1 text-sm font-medium text-gray-500 hover:text-gray-900 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

/**
 * The page title itself, exported for the one case that renders its own `h1`: a `titleSlot`.
 *
 * A slot that hand-rolled these classes would drift from the pages that do not use one, which is
 * the whole reason the header exists.
 */
export const PAGE_TITLE_CLASSES =
  'max-w-[680px] bg-gradient-to-r from-black to-[#666666] bg-clip-text text-3xl font-semibold tracking-tight text-transparent [text-wrap:balance]';

/**
 * The header every page opens with.
 *
 * One shape for all of them: an optional way back, then the title, then the subtitle — with the
 * page's actions pulled to the right of the title rather than stacked above it, so the first line
 * of a page is always the same line. `[text-wrap:balance]` and the 680 px cap keep a long title
 * from running the width of a desktop window.
 */
export const PageHeader: FC<PageHeaderProps> = ({ title, titleSlot, subtitle, backTo, actions, titleId }) => (
  <header className="flex flex-col gap-4">
    {backTo ? (
      <Link to={backTo.to} className={BACK_LINK_CLASSES}>
        <ArrowLeft size={16} aria-hidden="true" />
        {backTo.label}
      </Link>
    ) : null}
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-2">
        {titleSlot ?? (
          <h1 id={titleId} className={PAGE_TITLE_CLASSES}>
            {title}
          </h1>
        )}
        {subtitle ? <p className="m-0 text-base text-gray-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  </header>
);
